<template>
  <section
    data-cy="marketing-objects-view"
    :data-view-ready="!!searchClient">
    <div
      v-if="!searchClient"
      class="text-center">
      <coozzy-spinner />
    </div>
    <ais-instant-search
      v-else
      ref="instant-search"
      :search-client="searchClient"
      index-name="object_marketing"
      :routing="routing">
      <app-refresh ref="app-refresh" />
      <ais-stats>
        <template #default="{ nbHits }">
          <coozzy-page-title
            :title="$t('message.managementOverview.marketing')"
            :count="nbHits"
            :count-text="$t('message.managementOverview.objects')"
            :clear-filter="checkfilter"
            @clear-filter="clickOnClear" />
        </template>
      </ais-stats>
      <!-- Filter -->
      <div class="row">
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <ais-search-box>
            <template #default="{ currentRefinement }">
              <div>
                <debounced-algolia-search-box
                  ref="debouncedAlgolia"
                  data-cy="marketing-objects-search-input"
                  :default-value="currentRefinement"
                  :delay="300" />
              </div>
            </template>
          </ais-search-box>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="marketingStatus.status"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchStatus', items) }}
                <coozzy-multiselect
                  ref="searchStatusSelect"
                  v-model="searchStatus"
                  data-cy="marketing-objects-filter-status"
                  :options="items"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.status')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabelStatus"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="onRefine($event.value,refine)"
                  @remove="onRefine($event.value,refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="category"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchCategories', items) }}
                <coozzy-multiselect
                  v-model="searchCategories"
                  data-cy="marketing-objects-filter-category"
                  :options="items"
                  :multiple="true"
                  :placeholder="$t('message.onBoarding.buildings.objects.category.title')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabelCategory"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="onRefine($event.value,refine)"
                  @remove="onRefine($event.value,refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="rooms"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchRooms', items) }}
                <coozzy-multiselect
                  v-model="searchRooms"
                  data-cy="marketing-objects-filter-rooms"
                  :options="items"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.rooms')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabel"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="onRefine($event.value,refine)"
                  @remove="onRefine($event.value,refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="assignees.marketing.name"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchAssignees', items) }}
                <coozzy-assignee-multiselect
                  v-model="searchAssignees"
                  data-cy="marketing-objects-filter-assignee"
                  :options="sortedFilterValues(getAssigneeData(items), customLabelAssignee)"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.assignee')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabelAssignee"
                  :group-select="true"
                  :track-by="'value'"
                  label="name"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="onRefine($event.value,refine)"
                  @remove="onRefine($event.value,refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="requestStatistics.status"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchRequestStatus', items) }}
                <coozzy-multiselect
                  v-model="searchRequestStatus"
                  data-cy="marketing-objects-filter-requests"
                  :options="items"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.request')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabelRequestStatus"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="onRefine($event.value,refine)"
                  @remove="onRefine($event.value,refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0 mt-md-1">
          <ais-refinement-list
            attribute="property.name"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchProperty', items) }}
                <coozzy-multiselect
                  v-model="searchProperty"
                  data-cy="marketing-objects-filter-property"
                  :options="items"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.property')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabelProperty"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="onRefine($event.value,refine)"
                  @remove="onRefine($event.value,refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0 mt-md-1">
          <ais-refinement-list
            attribute="owner.name"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchAdminOwner', items) }}
                <coozzy-multiselect
                  v-model="searchAdminOwner"
                  data-cy="marketing-objects-filter-owner"
                  :options="items"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.owner')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabel"
                  :track-by="'value'"
                  label="name"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="selectValue($event.value, items, refine)"
                  @remove="removeValue($event.value, items, refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mt-0 mt-md-1 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="networkPartnerAsset.name"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchAssetManager', items) }}
                <coozzy-multiselect
                  v-model="searchAssetManager"
                  data-cy="marketing-objects-filter-asset-management"
                  :options="sortOptions(items)"
                  :multiple="true"
                  :placeholder="$t('message.networkPartner.subTypes.asset_management')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabel"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="refine($event.value)"
                  @remove="refine($event.value)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mt-0 mt-md-1 mb-2 mb-md-0">
          <ais-refinement-list
            attribute="networkPartnerAdministration.name"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchAdministration', items) }}
                <coozzy-multiselect
                  v-model="searchAdministration"
                  data-cy="marketing-objects-filter-administration"
                  :options="getAdministrationData(items)"
                  :multiple="true"
                  :placeholder="$t('message.tableColumnHeadings.administration')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabelAssignee"
                  :track-by="'value'"
                  label="name"
                  group-values="employees"
                  group-label="employee"
                  :group-select="true"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="selectValue($event.value, items, refine)"
                  @remove="removeValue($event.value, items, refine)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
        <div class="col-12 col-md-2 mt-0 mt-md-1">
          <ais-refinement-list
            attribute="networkPartnerAccounting.name"
            :limit="100">
            <template #default="{items, refine, searchForItems}">
              <div>
                {{ defaultRefining('searchAccounting', items) }}
                <coozzy-multiselect
                  v-model="searchAccounting"
                  data-cy="marketing-objects-filter-accounting"
                  :options="sortOptions(items)"
                  :multiple="true"
                  :placeholder="$t('message.networkPartner.subTypes.accounting')"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :custom-label="customLabel"
                  :track-by="'value'"
                  :preselect-first="false"
                  :taggable="false"
                  @search-change="searchForItems($event)"
                  @select="refine($event.value)"
                  @remove="refine($event.value)" />
              </div>
            </template>
          </ais-refinement-list>
        </div>
      </div>
      <!-- eslint-disable vue/attribute-hyphenation -->
      <ais-configure
        :hitsPerPage="nbrPerPage"
        :filters="filterQuery" />
      <!-- eslint-disable vue/attribute-hyphenation -->
      <div
        id="rowConfig"
        class="row mt-2 align-items-center">
        <div
          v-if="linkMail === ''"
          class="col-md-2 col-sm-6 col-12">
          <coozzy-dropdown
            design="green"
            size="sm"
            class="w-100"
            data-cy="marketing-objects-selected-entries-button"
            :text="selectedEntriesText">
            <coozzy-dropdown-item
              v-if="isTicketingEnabled && (isEditor || isTicketEditor)"
              data-cy="marketing-objects-selected-create-ticket-item"
              :disabled="selectedEntries.length === 0"
              @click="createTicket">
              {{ $t('message.ticketOverview.createTicket') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="marketing-objects-selected-create-reminder-item"
              :disabled="selectedEntries.length === 0"
              @click="selectedEntries.length !== 0 ? $refs['reminder-creation'].show() : ''">
              {{ $t('message.calendar.reminder.create') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-space-divider />
            <coozzy-dropdown-item
              data-cy="marketing-objects-selected-set-assignee-item"
              :disabled="selectedEntries.length === 0"
              @click="openSetAssigneeModal(null)">
              {{ $t('message.marketingObjects.setAssignee') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="marketing-objects-selected-open-expose-item"
              :disabled="selectedEntries.length === 0"
              @click="$bvModal.show('openExposeModal')">
              {{ $t('message.advertisement.action.openExpose') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-space-divider />
            <coozzy-dropdown-item
              data-cy="marketing-objects-selected-remove-from-marketing-item"
              :disabled="selectedEntries.length === 0"
              @click="openRemoveFromMarketingModal">
              {{ $t('message.marketingObjects.removeFromMarketing') }}
            </coozzy-dropdown-item>
          </coozzy-dropdown>
        </div>
        <div class="col-md-2">
          <template v-if="selectAllVisibleEntries && nbrTotalOfHits > nbrPerPage && nbrTotalOfHits <= 1000">
            <coozzy-form-checkbox
              v-model="selectAllEntries"
              data-cy="marketing-objects-select-all-hidden-checkbox"
              :initial="selectAllEntries"
              :class="'p-0 w-100 btn-height-auto'"
              name="selectAllEntries"
              button>
              {{ selectAllEntries ? $t('message.generic.unselectHiddenEntries', { amount: nbrTotalOfHits }) : $t('message.generic.selectHiddenEntries', { amount: nbrTotalOfHits }) }}
            </coozzy-form-checkbox>
          </template>
          <ais-clear-refinements :included-attributes="['query', 'marketingStatus.status', 'category', 'rooms', 'assignees.marketing.name', 'requestStatistics.status','property.name', 'owner.name', 'assignees.administration.name', 'networkPartnerAsset.name', 'networkPartnerAdministration.name', 'networkPartnerAccounting.name']">
            <template #default="{ canRefine, refine }">
              <div>
                {{ setCanRefine(canRefine) }}
                <a
                  v-if="canRefine"
                  ref="clearClick"
                  @click="initFilter(refine)" />
              </div>
            </template>
          </ais-clear-refinements>
        </div>
        <div
          v-if="isCompanyAdmin === true"
          :class="linkMail === '' ? 'offset-md-5' : 'offset-md-7'"
          class="col-md-1 d-flex justify-content-end align-items-center">
          <div
            data-cy="marketing-objects-assemble-list-button"
            @click="openAssembleListModal()">
            <coozzy-filters-icon
              v-b-tooltip="{container: '#rowConfig', title: $t('message.generic.tooltipAssembleList')}" />
          </div>
        </div>
        <div
          class="col-md-2 col-sm-6 col-12  mt-2 mt-sm-0"
          :class="!isCompanyAdmin && linkMail === '' ? 'offset-md-5' : !isCompanyAdmin && linkMail !== '' ? 'offset-md-7' : ''">
          <coozzy-form-select
            v-model="nbrPerPage"
            name="numberEntries"
            data-cy="marketing-objects-page-size-select"
            class="select-entries float-right">
            <option value="10">
              10 {{ $t('message.generic.entries') }}
            </option>
            <option value="50">
              50 {{ $t('message.generic.entries') }}
            </option>
            <option value="100">
              100 {{ $t('message.generic.entries') }}
            </option>
            <option value="500">
              500 {{ $t('message.generic.entries') }}
            </option>
            <option value="1000">
              1000 {{ $t('message.generic.entries') }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <ais-state-results>
        <template #default="{ hits, results }">
          {{ syncStateResults(results, hits) }}
          <b-table
            ref="objectsTable"
            primary-key="objectID"
            data-cy="marketing-objects-table"
            :busy="isBusy || exposeLoading"
            class="mt-2"
            bordered
            hover
            responsive="true"
            stacked="md"
            selectable
            select-mode="single"
            :fields="fields"
            :items="filteredHits(hits)"
            @row-clicked="onRowClicked"
            @row-middle-clicked="onMiddleClicked">
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <coozzy-spinner />
              </div>
            </template>
            <template #head(checkbox)>
              <coozzy-form-checkbox
                ref="header_checkbox"
                data-cy="marketing-object-select-all-checkbox"
                @change="(value) => { handleCheckboxesInsideTable(value) }" />
            </template>
            <template #head(status)>
              {{ $t('message.tableColumnHeadings.status') }}
            </template>
            <template #head(thumbnail)>
              {{ $t('message.generic.thumbnail') }}
            </template>
            <template #head(numericId)>
              {{ $t('message.tableColumnHeadings.numericId') }}
            </template>
            <template #head(publicId)>
              {{ $t('message.tableColumnHeadings.publicId') }}
            </template>
            <template #head(objectNumber)>
              {{ $t('message.tableColumnHeadings.objectNumber') }}
            </template>
            <template #head(address)>
              {{ $t('message.tableColumnHeadings.address') }}
            </template>
            <template #head(type)>
              {{ $t('message.tableColumnHeadings.type') }}
            </template>
            <template #head(floor)>
              {{ $t('message.tableColumnHeadings.floor') }}
            </template>
            <template #head(rooms)>
              {{ $t('message.tableColumnHeadings.rooms') }}
            </template>
            <template #head(area)>
              {{ $t('message.tableColumnHeadings.area') }}
            </template>
            <template #head(assignee)>
              {{ $t('message.tableColumnHeadings.assignee') }}
            </template>
            <template #head(request)>
              {{ $t('message.tableColumnHeadings.request') }}
            </template>
            <template #head(appointment)>
              {{ $t('message.tableColumnHeadings.appointment') }}
            </template>
            <template #head(applicationForm)>
              {{ $t('message.tableColumnHeadings.applicationForm') }}
            </template>
            <template #head(netRent)>
              {{ $t('message.tableColumnHeadings.netRent') }}
            </template>
            <template #head(grossRent)>
              {{ $t('message.tableColumnHeadings.grossRent') }}
            </template>
            <template #head(viewingContact)>
              {{ $t('message.tableColumnHeadings.viewingContact') }}
            </template>
            <template #head(availableFrom)>
              {{ $t('message.tableColumnHeadings.availableFrom') }}
            </template>
            <template #head(objects)>
              {{ $t('message.tableColumnHeadings.objects') }}
            </template>

            <!-- Data -->
            <template
              #cell(checkbox)="data">
              <coozzy-form-checkbox
                :id="'check_' + data.item.objectID"
                data-cy="marketing-object-row-checkbox"
                @change="(value) => { itemSelected(data.item, value) }" />
            </template>
            <template #cell(status)="data">
              <div
                v-b-tooltip.hover.html="getOnlineStatusTooltipConfig(data.item)"
                data-cy="marketing-object-row-status"
                :class="calculateMarketingStatus(resolveMarketingStatus(data.item)).color">
                {{ calculateMarketingStatus(resolveMarketingStatus(data.item)).label }}
                <template v-if="calculateMarketingStatus(resolveMarketingStatus(data.item)).dateFuture">
                  <br>
                  {{ $t('message.generic.until') + ' ' + calculateMarketingStatus(resolveMarketingStatus(data.item)).dateFuture }}
                </template>
                <template v-else-if="calculateMarketingStatus(resolveMarketingStatus(data.item)).date">
                  <br>
                  {{ calculateMarketingStatus(resolveMarketingStatus(data.item)).date }}
                </template>
                <template v-if="data.item.marketingStatus.status.includes('reserved')">
                  <br>
                  {{ $t('message.manageInterestedParties.reserved') }}
                </template>
                <template v-if="data.item.marketingStatus.status.includes('rented')">
                  <template v-if="data.item.futureTenancy && data.item.futureTenancy.startingAt">
                    <br>
                    {{ data.item.futureTenancy.startingAt | objectToDate }}
                  </template>
                  <template v-else-if="data.item.tenancy && data.item.tenancy.startingAt">
                    <br>
                    {{ data.item.tenancy.startingAt | objectToDate }}
                  </template>
                  <br>
                  {{ $t('message.marketingObjects.status.' + data.item.inMarketingStatus) }}
                </template>
              </div>
            </template>
            <template #cell(thumbnail)="data">
              <coozzy-thumbnail
                v-if="data.item.images && data.item.images.length > 0"
                :src="sortImageByOrder(data.item.images)[0].thumbnailSUrl"
                :original="sortImageByOrder(data.item.images)[0].url" />
              <div
                v-else
                class="d-flex justify-content-center">
                <coozzy-thumbnail
                  class="thumbnail"
                  src="/img/image-placeholder.png"
                  alt="Placeholder" />
              </div>
            </template>
            <template #cell(numericId)="data">
              <span data-cy="marketing-object-row-numeric-id">
                {{ data.item.numericId | displayOptionalValue }}
              </span>
            </template>
            <template #cell(publicId)="data">
              <span data-cy="marketing-object-row-public-id">{{ data.item.publicId | displayOptionalValue }}</span>
            </template>
            <template #cell(objectNumber)="data">
              <span data-cy="marketing-object-row-object-number">{{ data.item.number | displayOptionalValue }}</span>
            </template>
            <template #cell(address)="data">
              <span data-cy="marketing-object-row-address">
                <address-text :address="data.item.address" />
              </span>
            </template>
            <template
              #cell(type)="data">
              <template v-if="data.item.category && data.item.category !== 'UNDEFINED_CATEGORY'">
                {{ $t('message.onBoarding.buildings.objects.category.' + data.item.category.toLowerCase()) }}
              </template>
              <template v-else>
                -
              </template>
            </template>
            <template
              #cell(floor)="data">
              <div class="text-center">
                {{ formatFloor(data.item.floor) }}
              </div>
            </template>
            <template
              #cell(rooms)="data">
              <div class="text-center">
                {{ data.item.rooms | displayOptionalValue }}
              </div>
            </template>
            <template
              #cell(area)="data">
              <div class="text-center">
                {{ getLA(data.item) | formatArea }}
              </div>
            </template>
            <template #cell(assignee)="data">
              <div class="text-center">
                <coozzy-button
                  data-cy="marketing-object-row-assignee-button"
                  design="default"
                  size="small"
                  @click="openSetAssigneeModal(data.item)">
                  {{
                    data.item.assignees && data.item.assignees.marketing && data.item.assignees.marketing.name && data.item.assignees.marketing.name !== 'Unassigned Nicht zugeordnet Non assegnato' ? data.item.assignees.marketing.name : '-'
                  }}
                </coozzy-button>
              </div>
            </template>
            <template #cell(request)="data">
              <div class="row justify-content-start badgeFilter">
                <div class="col-4 text-center-badge badgeOverall">
                  {{ $t('message.manageInterestedParties.status.all') }}:
                </div>
                <div class="col-3 badgeFilter">
                  <coozzy-badge
                    size="large"
                    class="badgeOverall"
                    design="grey">
                    {{ data.item.requestStatistics.overall }}
                  </coozzy-badge>
                </div>
              </div>
              <div class="row justify-content-start mt-1 badgeFilter">
                <div class="col-4 text-center-badge badgeNew">
                  {{ $t('message.manageInterestedParties.status.new') }}:
                </div>
                <div class="col-3 badgeFilter">
                  <coozzy-badge
                    size="large"
                    class="badgeNew"
                    :design="data.item.requestStatistics.new > 0 ? 'red' : 'grey'">
                    {{ data.item.requestStatistics.new }}
                  </coozzy-badge>
                </div>
              </div>
              <div class="row justify-content-start mt-1 badgeFilter">
                <div class="col-4 text-center-badge badgeResponse">
                  {{ $t('message.manageInterestedParties.status.responses_short') }}:
                </div>
                <div class="col-3 badgeFilter">
                  <coozzy-badge
                    size="large"
                    class="badgeResponse"
                    :design="data.item.requestStatistics.unread > 0 ? 'red' : 'grey'">
                    {{ data.item.requestStatistics.unread }}
                  </coozzy-badge>
                </div>
              </div>
            </template>
            <template #cell(appointment)="data">
              <div class="badgeFilter">
                <div class="badgeDone badgeDone-title">
                  {{ $t('message.onBoarding.buildings.statistics.done') }}:
                </div>
                <coozzy-badge
                  class="p-1 badgeDone"
                  size="large"
                  design="grey">
                  {{ data.item.requestStatistics.appointmentsDone }}
                </coozzy-badge>
              </div>
            </template>
            <template #cell(applicationForm)="data">
              <div class="badgeFilter">
                <div class="badgeReceived badgeReceived-title">
                  {{ $t('message.onBoarding.buildings.statistics.received') }}:
                </div>
                <coozzy-badge
                  class="p-1 badgeReceived"
                  size="large"
                  design="grey">
                  {{ data.item.requestStatistics.applicationsReceived }}
                </coozzy-badge>
              </div>
            </template>
            <template
              #cell(netRent)="data">
              <div class="text-center">
                {{ data.item.pricing.netRent | formatPrice }}
              </div>
            </template>
            <template
              #cell(grossRent)="data">
              <div class="text-center">
                {{ data.item.pricing.grossRent | formatPrice }}
              </div>
            </template>
            <template
              #cell(objects)="data">
              <div class="text-center">
                {{ data.item.name | displayOptionalValue }}
              </div>
            </template>
            <template #cell(viewingContact)="data">
              <template v-for="vc in [resolveViewingContact(data.item)]">
                <router-link
                  v-if="vc.contactId"
                  :key="'link-' + vc.contactId"
                  :to="{ name: 'AdminContactDetailsView', params: { id: vc.contactId } }">
                  <span v-if="vc.name">
                    {{ vc.name }}
                  </span>
                  <span v-else>
                    -
                  </span>
                </router-link>
                <span
                  v-else-if="vc.name"
                  :key="'name-' + vc.name">
                  {{ vc.name }}
                </span>
                <span
                  v-else
                  :key="'empty-' + (vc.contactId || vc.name || 'none')">
                  -
                </span>
              </template>
            </template>
            <template #cell(availableFrom)="data">
              <span v-if="loadedObjectsById[data.item.objectID] && loadedObjectsById[data.item.objectID].availability && loadedObjectsById[data.item.objectID].availability.availableFrom > 0">{{ loadedObjectsById[data.item.objectID].availability.availableFrom | timestampToDate }}</span>
              <span v-else>-</span>
            </template>
          </b-table>
        </template>
      </ais-state-results>
      <pagination-algolia
        ref="paginator"
        @changed-page="clearCheckbox()" />
    </ais-instant-search>
    <set-advert-relations
      ref="setAssigneeModal"
      :title="$t('message.advertisementsOverview.setAssignee')"
      :element-list="contactList"
      type="employee"
      @save="setAssignee" />
    <b-modal
      :id="'confirmationModal'"
      ref="confirmationModal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingObjects.confirmModal.confirmationTitle')">
      <div class="col pl-0">
        <p>{{ textConfirmation }}</p>
      </div>
      <div class="col pl-0">
        <coozzy-button
          size="small"
          class="mb-0 border"
          design="transparent"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          v-if="actionPublish === 'publish'"
          size="small"
          class="float-right mb-0"
          design="green"
          @click="setPublish()">
          {{ $t('message.marketingObjects.publish') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      :id="'removeFromMarketingModal'"
      ref="removeFromMarketingModal"
      data-cy="marketing-objects-remove-modal"
      :data-view-ready="!removeFromMarketingLoading"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingObjects.confirmModal.removeFromMarketingTitle')">
      <div
        v-if="removeFromMarketingLoading"
        class="col text-center">
        <coozzy-spinner class="m-3" />
      </div>
      <template v-else>
        <div class="col pl-0">
          <p>
            {{ $t('message.marketingObjects.confirmModal.removeFromMarketingText') }}
          </p>
        </div>
        <div class="col pl-0">
          <coozzy-button
            data-cy="marketing-objects-remove-cancel-button"
            size="small"
            class="mb-0 border"
            design="transparent"
            :disabled="removeFromMarketingLoading"
            @click="$bvModal.hide('removeFromMarketingModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="marketing-objects-remove-confirm-button"
            size="small"
            class="float-right mb-0"
            design="green"
            :disabled="removeFromMarketingLoading"
            @click="removeFromMarketing()">
            {{ $t('message.generic.confirm') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <reminder-creation-modal
      ref="reminder-creation"
      :references-ids="selectedEntriesIds"
      :source="'object'"
      @new-reminder="clearCheckbox" />
    <b-modal
      :id="'openExposeModal'"
      ref="openExposeModal"
      data-cy="marketing-objects-open-expose-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.advertisement.action.openExpose')">
      <template>
        <div class="col-12">
          <p>{{ $t('message.advertisement.action.titleModal') }}</p>
        </div>
        <div class="col-12 mt-3 mb-3">
          <coozzy-form-select
            v-model="language"
            data-cy="marketing-objects-open-expose-language-select">
            <option
              v-for="option in optionsLanguages"
              :key="option.key"
              :value="option.key">
              {{ option.value }}
            </option>
          </coozzy-form-select>
        </div>
        <div class="col">
          <coozzy-button
            data-cy="marketing-objects-open-expose-cancel-button"
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="$bvModal.hide('openExposeModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="marketing-objects-open-expose-confirm-button"
            size="small"
            class="float-right mb-0"
            design="green"
            @click="openAdvertExpose">
            {{ $t('message.generic.confirm') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <assemble-list-modal
      ref="assembleListModal"
      modal-id="assembleListModal"
      :fields="selectedFields"
      :default-fields="dataFields"
      @save-fields="saveFields" />
  </section>
</template>

<script>
import ContactApi from '../misc/apis/ContactApi'
import AddressText from '../framework/components/misc/AddressText'
import { onboarding } from '@/mixins/onboarding'
import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import { algolia } from '@/mixins/algolia'
import ObjectApi from '../misc/apis/ObjectApi'
import Vue from 'vue'
import { tables } from '@/mixins/tables'
import CoozzyFormCheckbox from '../framework/components/form/checkbox/CoozzyFormCheckbox'
import SetAdvertRelations from '@/marketingBasic/components/advertisements/management/SetAdvertRelations.vue'
import CoozzyButton from '../framework/components/button/CoozzyButton'
import AppRefresh from '../framework/components/RefreshAlgolia'
import DebouncedAlgoliaSearchBox from '../framework/components/DebouncedAlgoliaSearchBox'
import PaginationAlgolia from '../framework/components/PaginationAlgolia'
import CoozzyBadge from '../framework/components/badge/CoozzyBadge'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle'
import CoozzyAssigneeMultiselect from '../framework/components/multiselect/CoozzyAssigneeMultiselect'
import CoozzyMultiselect from '@/framework/components/multiselect/CoozzyMultiselect'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem'
import CoozzyDropdownSpaceDivider from '@/framework/components/dropdown/CoozzyDropdownSpaceDivider'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import SearchApi from '@/misc/apis/SearchApi'
import AssembleListModal from '@/contact/components/AssembleListModal'
import CoozzyThumbnail from '@/framework/components/img/CoozzyThumbnail'
import { user } from '@/mixins/user'
import ReminderCreationModal from '@/calendar/components/ReminderCreationModal'
import { mapActions, mapGetters } from 'vuex'
import RequestApi from '@/marketingBasic/api/RequestApi'
import CoozzyFiltersIcon from '@/framework/components/icons/CoozzyFiltersIcon.vue'
export default {
  name: 'MarketingObjectsView',
  components: {
    CoozzyFiltersIcon,
    CoozzyThumbnail,
    CoozzyFormSelect,
    CoozzyDropdownSpaceDivider,
    CoozzyDropdownItem,
    CoozzyDropdown,
    CoozzyMultiselect,
    CoozzyPageTitle,
    CoozzySpinner,
    AddressText,
    CoozzyAssigneeMultiselect,
    AssembleListModal,
    DebouncedAlgoliaSearchBox,
    AppRefresh,
    PaginationAlgolia,
    CoozzyButton,
    CoozzyFormCheckbox,
    SetAdvertRelations,
    CoozzyBadge,
    ReminderCreationModal
  },
  mixins: [user, onboarding, obj, formatting, algolia, tables],
  beforeRouteLeave(to, from, next) {
    // InstantSearch writes filters via history.pushState (outside Vue Router). Capture from
    // history.state + window.location before menu navigation replaces the URL.
    const historyState = window.history.state
    if (historyState && typeof historyState === 'object') {
      this.persistCurrentAlgoliaFiltersFromRouteState(historyState)
    }
    this.persistAlgoliaFiltersFromLocation()
    next()
  },
  props: {
    linkMail: {
      type: String,
      default: ''
    },
    linkEmail: {
      type: String,
      default: ''
    },
    linkContactId: {
      type: String,
      default: ''
    },
    linkRequest: {
      type: String,
      default: ''
    },
    originalObjectId: {
      type: String,
      default: ''
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.navigation.marketing.title')
    }
  },
  data() {
    return {
      isBusy: false,
      checkfilter: false,
      showFirst: false,
      searchStatus: [],
      searchRooms: [],
      searchAssignees: [],
      searchRequestStatus: [],
      searchAdminOwner: [],
      searchProperty: [],
      searchAssetManager: [],
      searchAdministration: [],
      searchAccounting: [],
      galleryImages: [],
      contactList: [],
      textConfirmation: '',
      actionPublish: null,
      selectedObjectIds: null,
      selectedEntries: [],
      searchCategories: [],
      searchMarketer: [],
      removeFromMarketingLoading: false,
      objectToLink: null,
      contactToLink: null,
      exposeLoading: false,
      language: 'EN',
      selectedFields: [],
      fields: [],
      dataFields: [
        {
          key: 'status',
          label: this.$t('message.tableColumnHeadings.status'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'thumbnail',
          label: this.$t('message.generic.thumbnail'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center',
          selected: true
        },
        {
          key: 'numericId',
          label: this.$t('message.tableColumnHeadings.numericId'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'publicId',
          label: this.$t('message.tableColumnHeadings.publicId'),
          thClass: 'align-middle text-right',
          tdClass: 'align-middle text-right',
          selected: true
        },
        {
          key: 'objectNumber',
          label: this.$t('message.tableColumnHeadings.objectNumber'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false
        },
        {
          key: 'address',
          label: this.$t('message.tableColumnHeadings.address'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'type',
          label: this.$t('message.tableColumnHeadings.type'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'floor',
          label: this.$t('message.tableColumnHeadings.floor'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center',
          selected: true
        },
        {
          key: 'rooms',
          label: this.$t('message.tableColumnHeadings.rooms'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center',
          selected: true
        },
        {
          key: 'area',
          label: this.$t('message.tableColumnHeadings.area'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center',
          selected: true
        },
        {
          key: 'assignee',
          label: this.$t('message.tableColumnHeadings.assignee'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center',
          selected: true
        },
        {
          key: 'request',
          label: this.$t('message.tableColumnHeadings.request'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'appointment',
          label: this.$t('message.tableColumnHeadings.appointment'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'applicationForm',
          label: this.$t('message.tableColumnHeadings.applicationForm'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'netRent',
          label: this.$t('message.tableColumnHeadings.netRent'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false
        },
        {
          key: 'grossRent',
          label: this.$t('message.tableColumnHeadings.grossRent'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false
        },
        {
          key: 'availableFrom',
          label: this.$t('message.tableColumnHeadings.availableFrom'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false
        },
        {
          key: 'viewingContact',
          label: this.$t('message.tableColumnHeadings.viewingContact'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false
        },
        {
          key: 'objects',
          label: this.$t('message.tableColumnHeadings.objects'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: false
        }
      ],
      paramsAlgolia: {
        query: '',
        params: '',
        index: ''
      },
      nbrTotalOfHits: 0,
      selectAllVisibleEntries: false,
      selectAllEntries: false,
      allEntries: [],
      loadedObjectsById: {},
      // Display names for viewing contacts resolved from live object contact IDs (COOZ-15763)
      viewingContactNamesById: {}
    }
  },
  computed: {
    optionsLanguages() {
      return [
        { key: 'EN', value: this.$t('message.language.english') },
        { key: 'DE', value: this.$t('message.language.german') },
        { key: 'IT', value: this.$t('message.language.italian') }]
    },
    getUserLang() {
      return this.$store.getters['user/getCurrentUser']['https://coozzy.ch/lang'].toUpperCase()
    },
    employeeList() {
      if (this.getEmployees().length > 0) {
        return this.getEmployees()
      }
      return []
    },
    selectedEntriesIds() {
      return this.selectAllEntries ? this.allEntries.map(x => x.objectID) : this.selectedEntries.map(obj => obj.objectID)
    },
    selectedEntriesText() {
      const count = this.selectAllEntries && this.selectAllVisibleEntries ? this.nbrTotalOfHits : this.selectedEntries.length
      return this.$t('message.generic.selectedEntries') + ':  ' + count
    },
    filterQuery() {
      let query = 'marketingStatus.inMarketing:true'

      if (!this.isPlatformEmployee) {
        query += ` AND networkPartners.combinedId:${this.accountId.toString()}-marketing`
      }

      return query
    }
  },
  watch: {
    checkfilter: function () {
      this.clearCheckbox()
    },
    nbrPerPage: function () {
      if (this.showFirst) {
        this.$nextTick(() => {
          if (this.$refs.paginator) {
            this.$refs.paginator.refresh()
          }
        })
      } else {
        this.$nextTick(() => {
          if (this.$refs.paginator) {
            this.$refs.paginator.redirect(this.$route.query.page)
          }
        })
      }
      this.showFirst = true
      this.handleCheckboxesInsideTable(false)
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
    },
    selectAllEntries: async function (newValue) {
      if (newValue) {
        this.handleCheckboxesInsideTable(newValue)
        // check if we already call algolia with the same params
        if (this.nbrTotalOfHits !== this.allEntries.length) {
          this.allEntries = await this.getAllAvailableDataByIndex(this.paramsAlgolia)
        }
      }
    }
  },
  created() {
    this._lastSavedAlgoliaFilters = this.getPersistedAlgoliaFilters()
    this.setupAlgoliaFilterPersistence()
  },
  mounted: function () {
    this.loadData()
  },
  beforeDestroy() {
    const historyState = window.history.state
    if (historyState && typeof historyState === 'object' && (window.location.pathname || '').includes('marketing')) {
      this.persistCurrentAlgoliaFiltersFromRouteState(historyState)
    }
    this.persistAlgoliaFiltersFromLocation()
    this.teardownAlgoliaFilterPersistence()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    normalizeUnixTimestamp(rawValue) {
      const parsedValue = Number(rawValue)
      if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        return null
      }
      return parsedValue > 9999999999 ? Math.floor(parsedValue / 1000) : Math.floor(parsedValue)
    },
    getLatestPublication(item) {
      const publications = item?.marketingStatus?.publications
      if (!publications || publications.length === 0) {
        return null
      }
      return publications[publications.length - 1]
    },
    resolveMarketingStatus(item) {
      const loadedObject = this.loadedObjectsById[item?.objectID]
      const resolved = loadedObject?.marketingStatus || item?.marketingStatus
      // Backend ObjectMarketingStatus has no `status` field; keep the Algolia-computed
      // rental status so the rented/reserved label is not lost (COOZ-15674).
      if (loadedObject?.marketingStatus && item?.marketingStatus?.status) {
        return { ...resolved, status: item.marketingStatus.status }
      }
      return resolved
    },
    isUnassignedViewingContactName(name) {
      return !name || name === 'Unassigned Nicht zugeordnet Non assegnato'
    },
    formatFreeTextViewingName(viewingAppointment) {
      if (!viewingAppointment) {
        return ''
      }
      return [viewingAppointment.firstName, viewingAppointment.lastName].filter(Boolean).join(' ').trim()
    },
    formatResolvedContactName(contact) {
      if (!contact) {
        return ''
      }
      if (contact.name) {
        return contact.name
      }
      return [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
    },
    // Prefer live object contacts/viewingAppointment over Algolia assignees.viewingAppointment (COOZ-15763).
    resolveViewingContact(item) {
      const loadedObject = this.loadedObjectsById[item?.objectID]
      const algoliaViewing = item?.assignees?.viewingAppointment
      const algoliaName = this.isUnassignedViewingContactName(algoliaViewing?.name) ? '' : (algoliaViewing?.name || '')

      if (loadedObject) {
        const viewingAppointment = loadedObject.viewingAppointment
        if (viewingAppointment?.contactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
          const freeTextName = this.formatFreeTextViewingName(viewingAppointment)
          return {
            contactId: '',
            name: freeTextName || algoliaName
          }
        }
        const contactId = loadedObject.contacts?.contactIdViewingAppointment || ''
        if (contactId) {
          let name = this.viewingContactNamesById[contactId] || ''
          if (!name && algoliaViewing?.contactId === contactId) {
            name = algoliaName
          }
          return { contactId, name }
        }
        return { contactId: '', name: '' }
      }

      return {
        contactId: algoliaViewing?.contactId || '',
        name: algoliaName
      }
    },
    getTooltipOnlineFromTimestamp(item) {
      const startMarketingAt = this.normalizeUnixTimestamp(item?.marketingStatus?.startMarketingAt)
      if (startMarketingAt) {
        return startMarketingAt
      }
      const publishedAtFromStatus = this.normalizeUnixTimestamp(item?.marketingStatus?.publishedAt)
      if (publishedAtFromStatus) {
        return publishedAtFromStatus
      }
      const latestPublication = this.getLatestPublication(item)
      return this.normalizeUnixTimestamp(latestPublication?.published)
    },
    getTooltipOnlineUntilTimestamp(item) {
      const latestPublication = this.getLatestPublication(item)
      return this.normalizeUnixTimestamp(latestPublication?.unPublished)
    },
    getPromotionEndTimestamp(specialPublication, promotionStartTimestamp) {
      const promotedUntil = this.normalizeUnixTimestamp(specialPublication?.promotedUntil)
      if (promotedUntil) {
        return promotedUntil
      }

      const promotedDays = Number(specialPublication?.promotedDays)
      if (!Number.isFinite(promotedDays) || promotedDays <= 0) {
        return null
      }

      const promotedAt = this.normalizeUnixTimestamp(specialPublication?.promotedAt)
      if (promotedAt) {
        return promotedAt + (promotedDays * 86400) - 1
      }

      if (promotionStartTimestamp) {
        return promotionStartTimestamp + (promotedDays * 86400) - 1
      }

      return null
    },
    getPromotionStartTimestamp(item, specialPublication) {
      const promotedAt = this.normalizeUnixTimestamp(specialPublication?.promotedAt)
      if (promotedAt) {
        return promotedAt
      }

      const latestPublication = this.getLatestPublication(item)
      const publishedAtFromPublication = this.normalizeUnixTimestamp(latestPublication?.published)
      if (publishedAtFromPublication) {
        return publishedAtFromPublication
      }

      const publishedAtFromStatus = this.normalizeUnixTimestamp(item?.marketingStatus?.publishedAt)
      if (publishedAtFromStatus) {
        return publishedAtFromStatus
      }

      return this.normalizeUnixTimestamp(item?.marketingStatus?.startMarketingAt)
    },
    formatMarketplacePeriod(fromTimestamp, untilTimestamp) {
      if (!fromTimestamp) {
        return ''
      }
      const fromDate = this.$options.filters.timestampToDate(fromTimestamp)
      if (untilTimestamp && untilTimestamp > 0) {
        return this.$t('message.marketingObjects.marketplaceDateRange', {
          from: fromDate,
          until: this.$options.filters.timestampToDate(untilTimestamp)
        })
      }
      return this.$t('message.marketingObjects.marketplaceDateFrom', { from: fromDate })
    },
    isNewhomePlatform(platform) {
      return platform === 'NEWHOME_PLATFORM' || (platform && platform.toLowerCase() === 'newhome')
    },
    buildMarketplaceLabel(platform, specialPublication, isNewhomeFirstSplit) {
      const baseLabel = this.$t('message.export.' + platform)
      if (this.isNewhomePlatform(platform) && isNewhomeFirstSplit) {
        return `${baseLabel} (${this.$t('message.marketingObjects.newhomeFirstBadge', { days: specialPublication.exclusiveDays })})`
      }
      return baseLabel
    },
    isUntranslatedLabel(label) {
      return typeof label === 'string' && label.startsWith('message.')
    },
    getPromotionTypeLabelKey(promotionType) {
      if (promotionType === 'PROMOTION_TYPE_TOP') {
        return 'message.export.newhomePromotionOptions.top'
      }
      if (promotionType === 'PROMOTION_TYPE_HIGHLIGHT') {
        return 'message.export.newhomePromotionOptions.highlight'
      }
      if (promotionType === 'PROMOTION_TYPE_SPECIAL') {
        return 'message.export.newhomePromotionOptions.special'
      }
      return ''
    },
    getPromotionLabel(specialPublication) {
      const promotionTypeLabelKey = this.getPromotionTypeLabelKey(specialPublication?.promotionType)
      let promotionLabel = promotionTypeLabelKey ? this.$t(promotionTypeLabelKey) : ''
      if (!promotionLabel || this.isUntranslatedLabel(promotionLabel)) {
        promotionLabel = specialPublication?.platform ? this.$t(`message.export.${specialPublication.platform}`) : ''
      }
      if (!promotionLabel || this.isUntranslatedLabel(promotionLabel)) {
        promotionLabel = this.$t('message.export.NEWHOME_PLATFORM')
      }
      if (!promotionLabel || this.isUntranslatedLabel(promotionLabel)) {
        promotionLabel = 'Newhome'
      }
      return promotionLabel
    },
    getTooltipMarketplaceRows(item) {
      const specialPublication = item?.marketingStatus?.specialPublication || {}
      const exportedPlatforms = Array.isArray(item?.exportPlatforms) ? item.exportPlatforms : []
      const fallbackPlatform = specialPublication?.platform ? [specialPublication.platform] : []
      const platforms = exportedPlatforms.length > 0 ? exportedPlatforms : fallbackPlatform
      const fromTimestamp = this.getTooltipOnlineFromTimestamp(item)
      const untilTimestamp = this.getTooltipOnlineUntilTimestamp(item)
      const isNewhomeFirstSplit = Boolean(
        specialPublication.exclusivity &&
        Number(specialPublication.exclusiveDays) > 0 &&
        platforms.some(platform => this.isNewhomePlatform(platform)) &&
        platforms.length > 1
      )
      const delayedFromTimestamp = isNewhomeFirstSplit && fromTimestamp
        ? fromTimestamp + (Number(specialPublication.exclusiveDays) * 86400)
        : fromTimestamp

      return platforms.map((platform) => {
        const platformFromTimestamp = this.isNewhomePlatform(platform) ? fromTimestamp : delayedFromTimestamp
        return {
          platform: platform,
          label: this.buildMarketplaceLabel(platform, specialPublication, isNewhomeFirstSplit),
          period: this.formatMarketplacePeriod(platformFromTimestamp, untilTimestamp)
        }
      })
    },
    getPromotionStatusLine(item) {
      const specialPublication = item?.marketingStatus?.specialPublication
      if (!specialPublication || !specialPublication.promotion || !specialPublication.promotedDays) {
        return ''
      }
      const promotionStartTimestamp = this.getPromotionStartTimestamp(item, specialPublication)
      if (!promotionStartTimestamp) {
        return ''
      }
      const promotedDays = Number(specialPublication?.promotedDays)
      if (!Number.isFinite(promotedDays) || promotedDays <= 0) {
        return ''
      }
      const nowTimestamp = this.getStartOfDayTimestamp()
      if (nowTimestamp < promotionStartTimestamp) {
        return ''
      }
      let remainingDays
      const promotedUntil = this.normalizeUnixTimestamp(specialPublication?.promotedUntil)
      if (promotedUntil) {
        remainingDays = Math.floor((promotedUntil - nowTimestamp) / 86400)
      } else {
        // Match ticket formula: promotionDays - (today - promotionStartDate).
        const elapsedDays = Math.floor((nowTimestamp - promotionStartTimestamp) / 86400)
        remainingDays = promotedDays - elapsedDays
      }
      const promotionLabel = this.getPromotionLabel(specialPublication)
      if (remainingDays < 0) {
        return `${promotionLabel}: ${this.$t('message.marketingObjects.promotionExpired')}`
      }
      return `${promotionLabel}: ${this.$t('message.marketingObjects.promotionDaysRemaining', { days: remainingDays })}`
    },
    algoliaFiltersStorageKey() {
      return 'algolia-filters-' + (this.$route.name || 'MarketingObjectsView')
    },
    getPersistedAlgoliaFilters() {
      return localStorage.getItem(this.algoliaFiltersStorageKey())
    },
    stripEphemeralAlgoliaParams(search) {
      if (!search) {
        return ''
      }
      const queryString = search.startsWith('?') ? search.slice(1) : search
      if (!queryString) {
        return ''
      }
      const params = new URLSearchParams(queryString)
      const keysToDelete = []
      params.forEach((_, key) => {
        // Search query must not persist across page visits (COOZ-15131).
        if (
          key === 'page' ||
          key.includes('[page]') ||
          key === 'predefined' ||
          key === 'query' ||
          key.includes('[query]')
        ) {
          keysToDelete.push(key)
        }
      })
      keysToDelete.forEach(key => params.delete(key))
      const cleaned = params.toString()
      return cleaned ? `?${cleaned}` : ''
    },
    stripQueryFromAlgoliaRouteState(routeState) {
      if (!routeState || typeof routeState !== 'object') {
        return routeState
      }
      const cleaned = { ...routeState }
      if (cleaned.object_marketing && typeof cleaned.object_marketing === 'object') {
        const indexState = { ...cleaned.object_marketing }
        delete indexState.query
        cleaned.object_marketing = indexState
      }
      return cleaned
    },
    searchStringToRouteQuery(search) {
      const cleaned = this.stripEphemeralAlgoliaParams(search)
      if (!cleaned) {
        return {}
      }
      const params = new URLSearchParams(cleaned.startsWith('?') ? cleaned.slice(1) : cleaned)
      const query = {}
      params.forEach((value, key) => {
        query[key] = value
      })
      return query
    },
    savePersistedAlgoliaFilters(search = window.location.search, routeState = null) {
      const cleaned = this.stripEphemeralAlgoliaParams(search)
      // Never auto-clear here: InstantSearch dispose / empty syncs must not wipe stored filters.
      // Clearing is only via clearPersistedAlgoliaFilters (initFilter).
      if (!cleaned || !cleaned.includes('object_marketing')) {
        return
      }
      localStorage.setItem(this.algoliaFiltersStorageKey(), cleaned)
      this._lastSavedAlgoliaFilters = cleaned
      if (routeState && this.hasAlgoliaFilterState(routeState)) {
        try {
          const stateWithoutQuery = this.stripQueryFromAlgoliaRouteState(routeState)
          localStorage.setItem(this.algoliaFiltersStorageKey() + '-state', JSON.stringify(stateWithoutQuery))
        } catch (e) {
          // Ignore quota / circular JSON issues
        }
      }
    },
    clearPersistedAlgoliaFilters() {
      localStorage.removeItem(this.algoliaFiltersStorageKey())
      localStorage.removeItem(this.algoliaFiltersStorageKey() + '-state')
      this._lastSavedAlgoliaFilters = null
    },
    persistAlgoliaFiltersFromLocation() {
      const search = window.location.search
      if (search && search.includes('object_marketing')) {
        this.savePersistedAlgoliaFilters(search)
        return
      }
      if (this._lastSavedAlgoliaFilters) {
        localStorage.setItem(this.algoliaFiltersStorageKey(), this._lastSavedAlgoliaFilters)
      }
    },
    hasAlgoliaFilterState(routeState) {
      const indexState = routeState && routeState.object_marketing
      if (!indexState || typeof indexState !== 'object') {
        return false
      }
      // Query alone is not a persisted filter; require facet / refinement state.
      return Object.keys(indexState).some(key => key !== 'query' && key !== 'page')
    },
    hasIntentionalSearchQuery() {
      if (this.linkMail || this.linkEmail || this.linkContactId || this.linkRequest) {
        return true
      }
      const query = this.$route.query || {}
      const queryKeys = Object.keys(query)
      if (queryKeys.some(key => key === 'predefined' || key.includes('contact') || key.includes('object_marketing'))) {
        return true
      }
      const search = window.location.search || ''
      return search.includes('object_marketing') || search.includes('predefined') || search.includes('contact')
    },
    restorePersistedAlgoliaFiltersIfNeeded() {
      if (this.hasIntentionalSearchQuery()) {
        return Promise.resolve()
      }
      const saved = this.getPersistedAlgoliaFilters()
      if (!saved) {
        return Promise.resolve()
      }
      const query = this.searchStringToRouteQuery(saved)
      if (!Object.keys(query).length) {
        return Promise.resolve()
      }
      const queryString = saved.startsWith('?') ? saved : `?${saved}`
      let historyState = window.history.state
      try {
        const savedState = localStorage.getItem(this.algoliaFiltersStorageKey() + '-state')
        if (savedState) {
          historyState = this.stripQueryFromAlgoliaRouteState(JSON.parse(savedState))
        }
      } catch (e) {
        // keep existing history.state
      }
      // Apply to the real browser URL InstantSearch parseURL reads. Do this first so the
      // subsequent Vue Router replace cannot race InstantSearch mounting on an empty query.
      const { pathname, hash, origin } = window.location
      window.history.replaceState(historyState, '', `${origin}${pathname}${queryString}${hash}`)
      return this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query
      }).catch(() => {})
    },
    persistCurrentAlgoliaFiltersFromRouteState(routeState) {
      if (!routeState || typeof routeState !== 'object') {
        return
      }
      // Query-only / incomplete writes must not overwrite saved facet filters.
      if (!this.hasAlgoliaFilterState(routeState)) {
        return
      }
      if (!this.routing || !this.routing.router || typeof this.routing.router.createURL !== 'function') {
        this.persistAlgoliaFiltersFromLocation()
        return
      }
      try {
        const url = this.routing.router.createURL(routeState)
        const search = new URL(url).search
        this.savePersistedAlgoliaFilters(search, routeState)
      } catch (e) {
        this.persistAlgoliaFiltersFromLocation()
      }
    },
    setupAlgoliaFilterPersistence() {
      if (!this.routing || !this.routing.router || typeof this.routing.router.write !== 'function') {
        return
      }
      this._originalAlgoliaRouterWrite = this.routing.router.write.bind(this.routing.router)
      this.routing.router.write = (routeState) => {
        // Persist first so leave/dispose cannot race the delayed pushState.
        // Skip incomplete (e.g. query-only) states so localStorage facets stay intact.
        this.persistCurrentAlgoliaFiltersFromRouteState(routeState)
        // Empty InstantSearch syncs on remount can wipe a restored URL before filters re-apply.
        // Allow clearing only when the user intentionally cleared filters.
        // Query-only states still write (deep-merge keeps facets in the URL).
        const hasFacetFilters = this.hasAlgoliaFilterState(routeState)
        const indexState = routeState && routeState.object_marketing
        const isEmptyOrMissing = !indexState || typeof indexState !== 'object' || !Object.keys(indexState).length
        if (isEmptyOrMissing) {
          if (this._clearingAlgoliaFilters) {
            this._originalAlgoliaRouterWrite(routeState)
            return
          }
          if ((window.location.search || '').includes('object_marketing')) {
            return
          }
        }
        this._originalAlgoliaRouterWrite(routeState)
        // Custom history pushState is delayed (~100ms); capture location after it settles.
        // Only refresh localStorage from location when facets are present (avoid query-only wipe).
        if (hasFacetFilters || this._clearingAlgoliaFilters) {
          if (this._persistLocationTimer) {
            clearTimeout(this._persistLocationTimer)
          }
          this._persistLocationTimer = setTimeout(() => {
            this.persistAlgoliaFiltersFromLocation()
            this._persistLocationTimer = null
          }, 250)
        }
      }
    },
    teardownAlgoliaFilterPersistence() {
      if (this._persistLocationTimer) {
        clearTimeout(this._persistLocationTimer)
        this._persistLocationTimer = null
      }
      if (this._persistFromResultsTimer) {
        clearTimeout(this._persistFromResultsTimer)
        this._persistFromResultsTimer = null
      }
      if (this._originalAlgoliaRouterWrite && this.routing && this.routing.router) {
        this.routing.router.write = this._originalAlgoliaRouterWrite
        this._originalAlgoliaRouterWrite = null
      }
    },
    async loadData() {
      // Must finish before InstantSearch mounts so the first parseURL sees restored filters.
      await this.restorePersistedAlgoliaFiltersIfNeeded()
      // One more sync in case Vue Router overwrote browser search during replace.
      const saved = this.getPersistedAlgoliaFilters()
      if (saved && !(window.location.search || '').includes('object_marketing')) {
        const { pathname, hash, origin } = window.location
        const queryString = saved.startsWith('?') ? saved : `?${saved}`
        let historyState = window.history.state
        try {
          const savedState = localStorage.getItem(this.algoliaFiltersStorageKey() + '-state')
          if (savedState) {
            historyState = this.stripQueryFromAlgoliaRouteState(JSON.parse(savedState))
          }
        } catch (e) {
          // keep existing history.state
        }
        window.history.replaceState(historyState, '', `${origin}${pathname}${queryString}${hash}`)
      }
      this.getContacts()
      await this.loadEmployees()
      await this.loadInstantSearch()
      this.$nextTick(function () {
        this.language = this.getUserLang || 'DE'
      })
      if (this.linkMail === '' || this.linkRequest === '') {
        this.fields.unshift(
          {
            key: 'checkbox',
            label: '',
            thClass: 'th-checkbox',
            tdClass: 'align-middle border-right-0 checkbox-cell'
          })
      }
      if (localStorage.getItem('managementObjectsConfig') !== null && localStorage.getItem('managementObjectsConfig') !== '') {
        const res = localStorage.getItem('managementObjectsConfig').split(',')
        for (let index = 0; index < res.length; index++) {
          const columnKey = res[index] === 'livingArea' ? 'area' : res[index]
          const item = this.dataFields.filter(x => x.key === columnKey)
          this.fields.push(item)
        }
      } else {
        this.fields.push(...this.dataFields.filter(x => x.key === 'status' || x.key === 'thumbnail' || x.key === 'numericId' || x.key === 'publicId' || x.key === 'address' || x.key === 'type' || x.key === 'floor' || x.key === 'rooms' || x.key === 'area' || x.key === 'assignee' || x.key === 'request' || x.key === 'appointment' || x.key === 'applicationForm' || x.key === 'netRent' || x.key === 'grossRent'))
      }
      if (this.linkContactId) {
        ContactApi.contactResolve([this.linkContactId])
          .then(response => {
            this.contactToLink = response.persons.concat(response.companies)[0]
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          })
      }
      SearchApi.loadSettings(this.accountId).then(response => {
        const settings = response.tableSettings
        if (settings.length > 0) {
          settings.forEach(setting => {
            if (setting.columns.length > 0 && setting.table === 'marketing_object_view') {
              this.loadFields(setting)
            }
          })
        }
      }).catch(e => {
        console.log(e)
      })
    },
    getOnlineStatusTooltipConfig(item) {
      if (this.calculateMarketingStatus(this.resolveMarketingStatus(item)).label !== 'Online') {
        return false
      }
      return {
        title: this.buildOnlineStatusTooltipHtml(item),
        customClass: 'tooltip-marketing',
        placement: 'bottom'
      }
    },
    escapeTooltipHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    buildOnlineStatusTooltipHtml(item) {
      let html = ''
      if (item.exportPlatforms && item.exportPlatforms.length > 0) {
        html += this.escapeTooltipHtml(this.$t('message.marketingObjects.onlineTooltip'))
        html += '<ul class="text-left mb-0">'
        this.getTooltipMarketplaceRows(item).forEach(platformLine => {
          html += `<li>${this.escapeTooltipHtml(platformLine.label)}`
          if (platformLine.period) {
            html += ` ${this.escapeTooltipHtml(platformLine.period)}`
          }
          html += '</li>'
        })
        html += '</ul>'
        const promotionLine = this.getPromotionStatusLine(item)
        if (promotionLine) {
          html += `<br>${this.escapeTooltipHtml(promotionLine)}`
        }
        html += '<br>'
      }
      if (item.marketingStatus?.startMarketingAt) {
        html += `${this.escapeTooltipHtml(this.$t('message.marketingObjects.startMarketing'))} : ${this.escapeTooltipHtml(this.$options.filters.timestampToDate(item.marketingStatus.startMarketingAt))}<br>`
      }
      if (item.marketingStatus?.marketingNote) {
        html += `${this.escapeTooltipHtml(this.$t('message.marketingObjects.marketingNote'))} :<br>${this.escapeTooltipHtml(item.marketingStatus.marketingNote)}`
      }
      return html
    },
    syncStateResults(result, hits) {
      const hitsKey = hits.map(h => h.objectID).join(',')
      const key = `${result.page}|${result.nbHits}|${result.query}|${hitsKey}`
      if (this._lastStateResultsKey === key) {
        return ''
      }
      this._lastStateResultsKey = key
      this.$nextTick(() => {
        this.applyStateResults(result)
        this.loadObjectsForHits(hits)
      })
      return ''
    },
    applyStateResults(result) {
      this.nbrTotalOfHits = result.nbHits
      const params = this.editAlgoliaParams(result.params)
      this.paramsAlgolia = {
        query: result.query,
        params: params,
        index: result.index,
        attributesToRetrieve: ['objectID']
      }
      // InstantSearch may push the URL asynchronously; persist after the location settles.
      if (this._persistFromResultsTimer) {
        clearTimeout(this._persistFromResultsTimer)
      }
      this._persistFromResultsTimer = setTimeout(() => {
        this.persistAlgoliaFiltersFromLocation()
        this._persistFromResultsTimer = null
      }, 300)
    },
    filteredHits(hits) {
      if (this.linkRequest !== '' && this.originalObjectId !== '') {
        return hits.filter(hit => {
          return hit.objectID !== this.originalObjectId
        })
      }
      return hits
    },
    async loadObjectsForHits(hits) {
      // Always fetch via API — Vuex allObjects/objects can be stale after advert edits in the
      // same session, and resolveViewingContact prefers loadedObjectsById over Algolia (COOZ-15763).
      const ids = [...new Set(
        hits.map(h => h.objectID).filter(id => id && !this.loadedObjectsById[id])
      )]
      if (ids.length > 0) {
        ids.forEach(id => this.$set(this.loadedObjectsById, id, null))
        try {
          const response = await ObjectApi.listObjectsByIds(ids)
          if (response && response.objects) {
            response.objects.forEach(obj => {
              this.$set(this.loadedObjectsById, obj.id, obj)
            })
          }
        } catch (e) {
          console.log(e)
        }
      }
      await this.resolveViewingContactNamesForHits(hits)
    },
    async resolveViewingContactNamesForHits(hits) {
      const idsToResolve = []
      for (const hit of hits) {
        const loadedObject = this.loadedObjectsById[hit?.objectID]
        if (!loadedObject) {
          continue
        }
        if (loadedObject.viewingAppointment?.contactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
          continue
        }
        const contactId = loadedObject.contacts?.contactIdViewingAppointment
        if (!contactId || this.viewingContactNamesById[contactId]) {
          continue
        }
        const algoliaViewing = hit?.assignees?.viewingAppointment
        if (
          algoliaViewing?.contactId === contactId &&
          !this.isUnassignedViewingContactName(algoliaViewing?.name)
        ) {
          this.$set(this.viewingContactNamesById, contactId, algoliaViewing.name)
          continue
        }
        idsToResolve.push(contactId)
      }
      const uniqueIds = [...new Set(idsToResolve)]
      if (uniqueIds.length === 0) {
        return
      }
      try {
        const response = await ContactApi.contactResolve(uniqueIds)
        const contacts = (response.persons || []).concat(response.companies || [])
        contacts.forEach(contact => {
          const name = this.formatResolvedContactName(contact)
          if (name) {
            this.$set(this.viewingContactNamesById, contact.id, name)
          }
          if (Array.isArray(contact.contactPersons)) {
            contact.contactPersons.forEach(person => {
              if (!uniqueIds.includes(person.id) || this.viewingContactNamesById[person.id]) {
                return
              }
              const personName = this.formatResolvedContactName(person)
              if (personName) {
                this.$set(this.viewingContactNamesById, person.id, personName)
              }
            })
          }
        })
      } catch (e) {
        console.log(e)
      }
    },
    createTicket() {
      const defaultAssignee = this.selectedEntries.every((val, i, arr) => val.networkPartnerAsset?.accountId === arr[0].networkPartnerAsset?.accountId)
        ? this.selectedEntries[0].networkPartnerAsset.accountId
: this.selectedEntries.every((val, i, arr) => val.networkPartnerAdministration?.accountId === arr[0].networkPartnerAdministration?.accountId)
          ? this.selectedEntries[0].networkPartnerAdministration.accountId
: ''
      this.$router.push({ name: 'MarketingTicketCreation', query: { fromBulk: true, ids: this.selectedEntriesIds, defaultAssignee: defaultAssignee } })
    },
    async openAdvertExpose() {
      if (this.selectedEntriesIds.length > 0) {
        for (const element of this.selectedEntriesIds) {
          this.exposeLoading = true
          this.$bvModal.hide('openExposeModal')
          const blob = new Blob([await ObjectApi.getMarketingExpose(element, this.language)], { type: 'application/pdf' })
          const blobURL = URL.createObjectURL(blob)
          window.open(blobURL)
          this.exposeLoading = false
        }
        this.clearCheckbox()
      }
    },
    getSortedHits(hits) {
      return hits.sort(function (a, b) {
        if (a.marketingStatus.status === 'rented') return 1
        if (b.marketingStatus.status === 'rented') return -1
        return 0
      })
    },
    getContacts() {
      ContactApi.getContacts(this.$store.getters['user/getAccountId'])
        .then(response => {
          const allContacts = response.persons.concat(response.companies)
          this.contactList = allContacts.filter(con => con.types.includes('EMPLOYEE'))
          this.contactList = this.contactList.filter(contact => {
            const matchingEmployee = this.employeeList
              .filter(employee => !employee.blocked)
              .find(employee => employee?.email?.toLowerCase()?.trim() === contact?.email?.toLowerCase()?.trim())

            return matchingEmployee !== undefined && matchingEmployee !== null
          })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        }).finally(() => {
          setTimeout(() => {
            this.$refs.debouncedAlgolia?.setFocusInput()
          }, 300)
        })
    },
    clickOnClear() {
      this.$refs.clearClick.click()
    },
    setCanRefine(canRefine) {
      this._pendingCanRefine = canRefine
      if (this._canRefineSyncScheduled) {
        return ''
      }
      this._canRefineSyncScheduled = true
      this.$nextTick(() => {
        this._canRefineSyncScheduled = false
        this.checkfilter = this._pendingCanRefine
      })
      return ''
    },
    defaultRefining(value, items) {
      if (this.executedRefinements.includes(value)) {
        return ''
      }
      if (!this._pendingDefaultRefiningItems) {
        this._pendingDefaultRefiningItems = {}
      }
      this._pendingDefaultRefiningItems[value] = items
      if (this._pendingDefaultRefining?.[value]) {
        return ''
      }
      if (!this._pendingDefaultRefining) {
        this._pendingDefaultRefining = {}
      }
      this._pendingDefaultRefining[value] = true
      this.$nextTick(() => {
        this._pendingDefaultRefining[value] = false
        if (this.executedRefinements.includes(value)) {
          return
        }
        const latestItems = this._pendingDefaultRefiningItems[value] || items
        if (value === 'searchEndingAtFilter') {
          this[value] = latestItems.filter(obj => obj.isRefined).map(x => x.value)
          this.defaultDateEndFilter = latestItems.filter(obj => obj.isRefined).map(x => x.value)
        } else if (value === 'searchStartingAtFilter') {
          this[value] = latestItems.filter(obj => obj.isRefined).map(x => x.value)
          this.defaultStartDateFilter = latestItems.filter(obj => obj.isRefined).map(x => x.value)
        } else if (value === 'searchDebitUntilFilter') {
          this[value] = latestItems.filter(obj => obj.isRefined).map(x => x.value)
          this.defaultDebitUntilFilter = latestItems.filter(obj => obj.isRefined).map(x => x.value)
        } else {
          this[value] = latestItems.filter(obj => obj.isRefined)
        }
        this.executedRefinements.push(value)
        delete this._pendingDefaultRefiningItems[value]
      })
      return ''
    },
    customLabelCategory(item) {
      return this.$t('message.onBoarding.buildings.objects.category.' + item.value.toLowerCase()) + ' (' + item.count + ')'
    },
    customLabelRequestStatus(item) {
      if (item.value === 'none') {
        return '-'
      }
      return `${this.$t('message.manageInterestedParties.status.' + item.value)} (${item.count})`
    },
    clearCheckbox() {
      this.selectedEntries = []
      if (this.linkEmail === '') {
        if (this.$refs.header_checkbox) {
          this.$refs.header_checkbox.checked = false
        }
        this.handleCheckboxesInsideTable(false)
      }
    },
    onRefine(item, refine) {
      this.isBusy = true
      refine(item)
      setTimeout(() => {
        this.isBusy = false
      }, 300)
    },
    selectValue(item, items, refine) {
      this.isBusy = true
      if (item === undefined) {
        if (this.searchAssignees.length > 0) {
          this.searchAssignees.map(a => a.value).forEach(element => {
            refine(element)
          })
        }
        const result = items.map(a => a.value)
        result.forEach(element => {
          refine(element)
        })
      } else {
        refine(item)
      }
      setTimeout(() => {
        this.isBusy = false
      }, 300)
    },
    removeValue(item, items, refine) {
      this.isBusy = true
      if (item === undefined) {
        const result = items.map(a => a.value)
        result.forEach(element => {
          refine(element)
          this.searchAssignees.length = 0
        })
      } else {
        refine(item)
      }
      setTimeout(() => {
        this.isBusy = false
      }, 300)
    },
    customLabel(item) {
      if (item.value === 'Unassigned Nicht zugeordnet Non assegnato') {
        return this.$t('message.generic.unassigned') + ' (' + item.count + ')'
      }
      return item.value + ' (' + item.count + ')'
    },
    customLabelProperty(item) {
      return `${item.value} (${item.count})`
    },
    customLabelStatus(item) {
      return this.$t('message.marketingObjects.status.' + item.value.toLowerCase()) + ' (' + item.count + ')'
    },
    getBadgeNavigationQuery(event) {
      const query = { view: 0 }
      if (event.srcElement.classList.contains('badgeOverall')) {
        query.filter = 'all'
      } else if (event.srcElement.classList.contains('badgeNew')) {
        query.filter = 'new'
      } else if (event.srcElement.classList.contains('badgeResponse')) {
        query.filter = 'responses'
      } else if (event.srcElement.classList.contains('badgeDone')) {
        query.filterProgress = 'viewingAppointmentDone'
      } else if (event.srcElement.classList.contains('badgeReceived')) {
        query.filterProgress = 'applicationFormReceived'
      }
      return query
    },
    onRowClicked(item, index, event) {
      if (event.srcElement.parentElement.classList.contains('badgeFilter')) {
        this.$router.push({
          name: 'MarketingObjectDetailsView',
          params: { id: item.objectID },
          query: this.getBadgeNavigationQuery(event)
        })
      } else if (event.srcElement.classList.contains('custom-checkbox') || event.srcElement.classList.contains('checkbox-cell')) {
        const element = document.getElementById('check_' + item.objectId)
        if (element) {
          this.itemSelected(item, !element.checked)
          element.checked = !element.checked
        }
      } else {
        if (this.linkEmail !== '' || this.linkMail !== '' || this.linkRequest !== '') {
          this.objectToLink = item
          if (this.linkRequest !== '') {
            this.linkRequestToObject()
          } else {
            this.$router.push({
              name: 'MarketingObjectDetailsView',
              params: { id: item.objectID },
              query: {
                view: 0,
                linkMail: this.linkMail,
                linkEmail: this.linkEmail
              }
            })
          }
        } else {
          this.navigateToOverview(item, index, event, false)
        }
      }
    },
    onMiddleClicked(item, index, event) {
      if (event.srcElement.parentElement.classList.contains('badgeFilter')) {
        const route = this.$router.resolve({
          name: 'MarketingObjectDetailsView',
          params: { id: item.objectID },
          query: this.getBadgeNavigationQuery(event)
        })
        window.open(route.href)
      } else {
        this.navigateToOverview(item, index, event, true)
      }
    },
    linkRequestToObject() {
      const promises = []
      const arrayRequest = this.linkRequest.split(',')
      arrayRequest.forEach(element => {
        promises.push(RequestApi.moveRequest(element, this.objectToLink.objectID))
      })
      Promise.all(promises)
        .then(() => {
          Vue.toasted.show(this.$t('message.successMessages.requesMoved'), { type: 'success' })
          if (arrayRequest.length > 1) {
            this.$router.push({ name: 'MarketingObjectDetailsView', params: { id: this.objectToLink.objectID }, query: { view: 0 } })
          } else {
            this.$router.push({ name: 'MarketingObjectDetailsView', params: { id: this.objectToLink.objectID }, query: { view: 0, elementSelected: arrayRequest[0] } })
          }
        })
    },
    handleCheckboxesInsideTable(value) {
      if (this.$refs.objectsTable) {
        const allCheckboxesComponents = this.$refs.objectsTable.$children[1].$children
        this.handleCheckbox(allCheckboxesComponents, value)
        this.selectAllVisibleEntries = value
        if (value === false) {
          this.selectAllEntries = false
        }
      }
    },
    initFilter(refine) {
      this.searchStatus = []
      this.searchRooms = []
      this.searchAssignees = []
      this.searchCategories = []
      this.searchAdminOwner = []
      this.searchProperty = []
      this.searchRequestStatus = []
      this.searchAssetManager = []
      this.searchAdministration = []
      this.searchAccounting = []
      this._clearingAlgoliaFilters = true
      this.clearPersistedAlgoliaFilters()
      refine()
      this.$nextTick(() => {
        this._clearingAlgoliaFilters = false
      })
    },
    itemSelected(item, checked) {
      if (checked) {
        this.selectedEntries.push(item)
        if (this.selectedEntries.length === this.nbrPerPage) {
          this.selectAllVisibleEntries = true
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter(entry => entry.objectID !== item.objectID)
        this.selectAllEntries = false
        this.selectAllVisibleEntries = false
      }
    },
    loadFields(setting) {
      const migratedSetting = {
        ...setting,
        columns: setting.columns.map(column => column === 'livingArea' ? 'area' : column)
      }
      const result = this.getFields(this.dataFields, migratedSetting)
      this.fields = result?.fields
      this.selectedFields = result?.selectedFields
      this.$refs.objectsTable?.refresh()
    },
    saveFields(data) {
      const columns = data.filter(x => x.selected === true).map(y => y.key)
      SearchApi.saveSettings(this.accountId, columns, 'marketing_object_view').then(response => {
        const setting = response.tableSettings
        if (setting && setting.columns.length > 0) {
          this.loadFields(setting)
        }
        this.$bvModal.hide('assembleListModal')
      })
    },
    openAssembleListModal() {
      this.$bvModal.show('assembleListModal')
    },
    navigateToOverview(item, index, event, newTab = false) {
      const element = event.srcElement
      if (element.tagName === 'IMG' && element.classList.contains('thumbnail')) {
        if (item.images.length > 0) {
          this.galleryImages = [this.sortImageByOrder(item.images).map(x => x.url)[0]]
          this.$nextTick(function () {
            this.$viewerApi({
              images: this.galleryImages,
              options: this.viewerOptions
            })
          })
        }
      } else {
        if (item.marketingStatus.newInMarketing === true) {
          ObjectApi.marketingMarkSeen(item.objectID)
            .catch(e => {
              console.log(e)
            })
        }
        if (!newTab) {
          this.$router.push({
            name: 'MarketingObjectDetailsView',
            params: { id: item.objectID },
            query: { view: 0 }
          })
        } else {
          const route = this.$router.resolve({
            name: 'MarketingObjectDetailsView',
            params: { id: item.objectID },
            query: { view: 0 }
          })
          window.open(route.href)
        }
      }
    },
    setPublish() {
      if (this.actionPublish === 'publish') {
        const publishEntries = this.selectAllEntries ? this.allEntries : this.selectedEntries
        Promise.all(publishEntries.map(entry => {
          const scheduled = this.getScheduledPublicationParams(entry?.marketingStatus)
          return ObjectApi.publishObject(
            [entry.objectID],
            scheduled.unPublishAt,
            scheduled.unPublishAtAmountOfRequests,
            scheduled.specialPublication,
            scheduled.publishAt
          )
        }))
          .then(() => {
            this.isBusy = true
            this.$refs.confirmationModal.hide()
            Vue.toasted.show(this.$t('message.successMessages.publishObject'), { type: 'success' })
            setTimeout(() => {
              if (this.$refs['app-refresh']) {
                this.$refs['app-refresh'].refresh()
              }
              setTimeout(() => {
                this.isBusy = false
              }, 1000)
            }, 1700)
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.publishObject'), { type: 'error' })
          })
      } else if (this.actionPublish === 'unPublish') {
        ObjectApi.unPublishObject(this.selectedEntriesIds)
          .then(() => {
            this.isBusy = true
            this.$refs.confirmationModal.hide()
            Vue.toasted.show(this.$t('message.successMessages.unPublishObject'), { type: 'success' })
            setTimeout(() => {
              if (this.$refs['app-refresh']) {
                this.$refs['app-refresh'].refresh()
              }
              setTimeout(() => {
                this.isBusy = false
              }, 1000)
            }, 1700)
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.unPublishObject'), { type: 'error' })
          })
      }
    },
    openSetAssigneeModal(item) {
      this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
      if (item === null) {
        this.selectedObjectIds = this.selectedEntriesIds
      } else {
        this.selectedObjectIds = [item.objectID]
      }
      this.$refs.setAssigneeModal.newElementId =
        item && item.assignees && item.assignees.marketing ? item.assignees.marketing.contactId || null : null
      this.$refs.setAssigneeModal.setModalShow = true
    },
    openRemoveFromMarketingModal() {
      this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
      this.$refs.removeFromMarketingModal.show()
    },
    removeFromMarketing() {
      this.removeFromMarketingLoading = true
      ObjectApi.removeFromMarketing(this.selectedEntriesIds)
        .then(() => {
          this.clearCheckbox()
          setTimeout(() => {
            if (this.$refs['app-refresh']) {
              this.$refs['app-refresh'].refresh()
            }
            this.removeFromMarketingLoading = false
            this.$refs.removeFromMarketingModal.hide()
            Vue.toasted.show(this.$t('message.successMessages.removeFromMarketing'), { type: 'success' })
          }, 2000)
        })
        .catch(e => {
          console.log(e)
        })
    },
    setAssignee(newEmployee) {
      this.$refs.setAssigneeModal.setModalDisabled = true
      ObjectApi.setMarketingAssignee(newEmployee ? newEmployee.id : null, this.selectedObjectIds)
        .then(() => {
          this.clearCheckbox()
          setTimeout(() => {
            if (this.$refs['app-refresh']) {
              this.$refs['app-refresh'].refresh()
            }
            Vue.toasted.show(this.$t('message.successMessages.setMarketingObjectAssignee'), { type: 'success' })
            this.$refs.setAssigneeModal.setModalShow = false
            this.$refs.setAssigneeModal.setModalDisabled = false
          }, 2000)
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.setMarketingObjectAssignee'), { type: 'error' })
        })
    },
    redirectToAllRequest() {
      this.$router.push({
        name: 'MarketingObjectDetailsView',
        params: { id: this.id },
        query: {
          view: 0,
          elementSelected: ''
        }
      })
    },
    sortImageByOrder(images) {
      const imgs = [...images]
      return imgs.sort((a, b) => (a.order > b.order ? 1 : -1))
    },
    sortOptions(item) {
      return item.sort(function(a, b) {
        if (a.value === b.value) return 0
        if (a.value === 'Unassigned Nicht zugeordnet Non assegnato') return 1
        if (b.value === 'Unassigned Nicht zugeordnet Non assegnato') return -1
        return 0
      })
    },
    getAdministrationData(items) {
      items.forEach(element => {
        const emp = this.employeeList.find(x => (x.profile.lastName + ' ' + x.profile.firstName) === element.label)
        if (emp) {
          element.active = !emp.blocked
        }
      })
      return [{
        employee: this.$t('message.search.filterAll'),
        employees: items
      }]
    },
    getAssigneeData(items) {
      items.forEach(element => {
        const emp = this.employeeList.find(x => (x.profile.lastName + ' ' + x.profile.firstName) === element.label)
        if (emp) {
          element.active = !emp.blocked
        }
      })
      return items
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(th button) {
  padding-right: 0 !important;
}

:deep(.span-dropdown) {
  padding: 0 !important;
}

.clickable-data {
  text-decoration: underline;
  color: $color-blue-dark;
}

.text-center-badge {
  margin-top: 0.125rem;
}
.badgeDone-title{
  display: inline !important;
}
.badgeReceived-title{
  display: inline !important;
}
</style>
